'use client'

import classnames from 'classnames'
import styles from './IframeSlideout.module.scss'
import useStore from '@/store'
import Icon from '@/components/Icon/Icon'
import { useContext, useEffect, useRef } from 'react'
import { ScrollContext } from '@/context/Scroll'
import { wait } from '@/utils/animation'
import gsap from 'gsap'

type IframeSlideoutProps = {
  className?: string
}

const IframeSlideout = ({ className }: IframeSlideoutProps) => {
  const iframeSlideoutData = useStore(state => state.iframeSlideoutData)
  const setIframeSlideoutData = useStore(state => state.setIframeSlideoutData)
  const { scroll } = useContext(ScrollContext)
  const bgRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const animateContent = (animateOut?: boolean, callback?: () => void) => {
    const duration = animateOut ? 0.3 : 0.6
    const ease = 'Power3.easeOut'
    gsap.killTweensOf([innerRef.current, bgRef.current])
    gsap.to(innerRef.current, {
      x: animateOut ? '105%' : 0,
      duration,
      ease,
    })
    gsap.to(bgRef.current, {
      autoAlpha: animateOut ? 0 : 0.6,
      duration,
      ease,
      onComplete: () => {
        if (!animateOut) {
          if (buttonRef.current) {
            buttonRef.current.focus()
          }
        }
        if (callback) callback()
      },
    })
  }

  const handleCloseClick = () => {
    animateContent(true, () => {
      setIframeSlideoutData({ open: false, url: '' })
    })
  }

  useEffect(() => {
    const fire = async () => {
      if (!iframeSlideoutData?.open) return
      await wait(10)
      animateContent()
    }

    fire()
  }, [iframeSlideoutData])

  useEffect(() => {
    if (!scroll) return
    if (iframeSlideoutData?.open) {
      scroll.stop()
    } else {
      scroll.start()
    }
  }, [iframeSlideoutData, scroll])

  if (!iframeSlideoutData?.open) return null

  return (
    <div className={classnames(styles.iframeSlideout, className)}>
      <div
        className={styles.bg}
        ref={bgRef}
        onClick={handleCloseClick}
      />
      <div
        className={styles.inner}
        ref={innerRef}
      >
        <div
          className={styles.content}
          data-lenis-prevent
        >
          <button
            ref={buttonRef}
            className={styles.closeButton}
            onClick={handleCloseClick}
          >
            <Icon
              name="close"
              className={styles.closeIcon}
            />
          </button>
          <div className={styles.iframeContainer}>
            {iframeSlideoutData.script ? (
              <div
                className={styles.iframeScriptWrapper}
                dangerouslySetInnerHTML={{ __html: iframeSlideoutData.script }}
              />
            ) : (
              <iframe
                src={iframeSlideoutData.url}
                style={{
                  border: '0px #ffffff none',
                  width: '100%',
                  height: '100%',
                }}
                scrolling="yes"
                frameBorder="1"
                allowFullScreen
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

IframeSlideout.displayName = 'IframeSlideout'

export default IframeSlideout
